import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

/**
 * Localizes navigation item labels using react-intl.
 *
 * All message descriptors live inside `intl.formatMessage()` calls so the
 * formatjs extractor can discover them and they execute within React intl context.
 *
 * Items whose id matches a known entry are translated; others
 * (e.g. brand names like "LuxPlus+" or "Société") pass through unchanged.
 */
function useLocalizedNavItems(navItems: Array<App.NavItem>): Array<App.NavItem> {
  const intl = useIntl()

  return useMemo(() => {
    const localizedTextById: Record<string, string> = {
      'nav-link-hotels': intl.formatMessage({
        id: 'qmng9YXOey',
        defaultMessage: 'Hotels',
        description: 'Navigation link label for hotels',
      }),
      'hotelsandresorts': intl.formatMessage({
        id: 'mv0NUuCN+J',
        defaultMessage: 'Hotels & Resorts',
        description: 'Navigation link label for hotels and resorts (white label brands)',
      }),
      'nav-link-homes-and-villas': intl.formatMessage({
        id: 'FA2jEOemcX',
        defaultMessage: 'Homes & Villas',
        description: 'Navigation link label for homes and villas',
      }),
      'nav-link-tours': intl.formatMessage({
        id: 'RxEg0l4AgD',
        defaultMessage: 'Tours',
        description: 'Navigation link label for tours',
      }),
      'nav-link-tours-cruises': intl.formatMessage({
        id: 'F1FG6iiJYO',
        defaultMessage: 'Tours & Cruises',
        description: 'Navigation link label for tours and cruises',
      }),
      'nav-link-cruises': intl.formatMessage({
        id: 'WxOXmR8UMx',
        defaultMessage: 'Cruises',
        description: 'Navigation link label for cruises',
      }),
      'nav-link-flights': intl.formatMessage({
        id: 'LqhX9TMuD6',
        defaultMessage: 'Flights',
        description: 'Navigation link label for flights',
      }),
      'nav-link-car-hire': intl.formatMessage({
        id: '+vjnPjP2Td',
        defaultMessage: 'Car Hire',
        description: 'Navigation link label for car hire',
      }),
      'nav-link-ultralux': intl.formatMessage({
        id: 'DhHKmA7TfB',
        defaultMessage: 'Ultra Lux',
        description: 'Navigation link label for ultra lux',
      }),
      'nav-link-experiences': intl.formatMessage({
        id: 'O3iGuX8w2W',
        defaultMessage: 'Experiences',
        description: 'Navigation link label for experiences',
      }),
      'nav-link-maldives-packages': intl.formatMessage({
        id: 'l1vgbKelVt',
        defaultMessage: 'Maldives Bundles',
        description: 'Navigation link label for Maldives bundles',
      }),
      'nav-link-inspiration': intl.formatMessage({
        id: 'JyY7Wc32Qd',
        defaultMessage: 'Inspiration',
        description: 'Navigation link label for inspiration',
      }),
      'nav-link-support': intl.formatMessage({
        id: 'DD61e49QUc',
        defaultMessage: 'Support',
        description: 'Navigation link label for support',
      }),
      'nav-link-store': intl.formatMessage({
        id: 'oHjaRz9hFh',
        defaultMessage: 'Visit Us In Store',
        description: 'Navigation link label for store visit',
      }),
      'nav-link-gift-cards': intl.formatMessage({
        id: 'ojq7uKvdZ3',
        defaultMessage: 'Gift Cards',
        description: 'Navigation link label for gift cards',
      }),
      'nav-link-insurance': intl.formatMessage({
        id: 'oJuyLLFpWQ',
        defaultMessage: 'Travel Protection',
        description: 'Navigation link label for travel protection',
      }),
      'nav-link-marketplace': intl.formatMessage({
        id: 'lPjr+VBoYM',
        defaultMessage: 'Shop',
        description: 'Navigation link label for shop/marketplace',
      }),
      'nav-link-refer': intl.formatMessage({
        id: '9L0hd0q7wl',
        defaultMessage: 'Refer a Friend',
        description: 'Navigation link label for refer a friend',
      }),
      'nav-link-le4b': intl.formatMessage({
        id: 'XgCsHuA7rI',
        defaultMessage: 'Business Travel',
        description: 'Navigation link label for business travel',
      }),
      'nav-link-golf': intl.formatMessage({
        id: 'hCpaCmGmyV',
        defaultMessage: 'Golf',
        description: 'Navigation link label for golf',
      }),
      'nav-link-groups': intl.formatMessage({
        id: 'g9qsxoGjQG',
        defaultMessage: 'Groups',
        description: 'Navigation link label for groups',
      }),
      'nav-link-weddings': intl.formatMessage({
        id: 'BxcNgvZS27',
        defaultMessage: 'Weddings',
        description: 'Navigation link label for weddings',
      }),
      'nav-link-more': intl.formatMessage({
        id: 'LTnN29sJm5',
        defaultMessage: 'More',
        description: 'Navigation link label for more dropdown',
      }),
      'nav-link-best-of-2025': intl.formatMessage({
        id: 'OsxT3mG8v7',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign',
      }),
      'nav-link-best-of-2025-eu': intl.formatMessage({
        id: 'OsxT3mG8v7',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign',
      }),
      'nav-link-best-of-2025-us': intl.formatMessage({
        id: 'OsxT3mG8v7',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign',
      }),
    }

    if (isAgentHubEnabled) {
      localizedTextById['nav-link-flights'] = intl.formatMessage({
        id: '2T5NKm1dJ+',
        defaultMessage: 'Direct Maldives Flights',
        description: 'Navigation link label for direct Maldives flights',
      })
    }

    const localize = (item: App.NavItem): App.NavItem => {
      let text: string | undefined
      if (item.id) {
        // Both "Tours" and "Tours & Cruises" share id "nav-link-tours" in
        // navItemsByRegion. Disambiguate using the source text so each gets
        // its own translation.
        if (item.id === 'nav-link-tours' && item.text === 'Tours & Cruises') {
          text = localizedTextById['nav-link-tours-cruises']
        } else {
          text = localizedTextById[item.id]
        }
      }
      if (!text && !item.items?.length) return item

      return {
        ...item,
        text: text ?? item.text,
        items: item.items?.map(localize),
      }
    }

    return navItems.map(localize)
  }, [navItems, intl])
}

export default useLocalizedNavItems
